import AppContext from 'App/AppContext';
import {
  IPButton,
  IPIcon,
  IPMessageBar,
  IPMessageBarActions,
  IPMessageBarBody,
  IPMessageBarIntent,
  IPSpinner,
  IPTooltip,
} from 'baseComponents';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isIdentityAuthError } from 'services/Auth/authErrors';
import Config from 'services/Config/configService';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import {
  getIdentityProviderErrorCode,
  isAbortOrCancelError,
  isAuthReloadError,
  isLoginError,
  isNetworkError,
  isTenantRestoreFailedError,
  isTenantRestoreInProgressError,
  isTenantSuspendedError,
} from './ErrorMessageHelper';
import { getErrorMessageText, saveErrorToApi } from './ErrorMessageService';

export enum ICustomErrorActionType {
  None = 0,
  Refresh = 1,
  Dismiss = 2,
}

interface IErrorMessageProps {
  error: AppError | undefined;
  setError?: (error: AppError | undefined) => void;
  //when provided, this function is called to refresh the app instead of reloading the page and calling setError
  customAction?: (actionType: ICustomErrorActionType) => Promise<void>;
}

//
// The error bar of the app (appContext.setError) and of the unhandled-error screen. The text comes from
// ErrorMessageService; this decides how severe it looks and whether the user gets a refresh action.
//
export const ErrorMessage = (props: IErrorMessageProps) => {
  const appContext = useContext(AppContext);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  // 'exceptions' and 'identityproviders' render the typed refusals of the API and of the identity layer;
  // this is the global error renderer, so those namespaces load whichever scene mounted it.
  const { t } = useTranslation(['translation', 'exceptions', 'identityproviders']);
  const [err, setErr] = useState<AppError | undefined>(undefined);

  useEffect(() => {
    if (props.error) {
      if (!err?.isEqual(props.error)) {
        setErr(props.error);
        logger.error(props.error);
        saveErrorToApi(props.error, appContext, t);
      }
    } else {
      setErr(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.error]);

  const reloadPage = () => {
    setIsRefreshing(true);
    window.location.reload();
  };

  const refreshPageButton = (label: string) => {
    return (
      <IPButton
        disabled={isRefreshing}
        icon={isRefreshing ? <IPSpinner size="tiny" /> : undefined}
        onClick={async () => {
          if (props.customAction) {
            await props.customAction(ICustomErrorActionType.Refresh);
          } else {
            if (props.setError) props.setError(undefined);
            reloadPage();
          }
        }}
      >
        {label}
      </IPButton>
    );
  };

  //
  // Main render
  //
  if (props.error === undefined) {
    return null;
  }

  const msg = getErrorMessageText(props.error, t);
  let action: JSX.Element | undefined;
  let intent: IPMessageBarIntent = 'error';

  const showDismiss = props.setError !== undefined || props.customAction !== undefined;

  if (isTenantRestoreInProgressError(props.error)) {
    //a restore of the organization runs and locks it: refreshing after it has finished brings the user back in
    intent = 'warning';
    action = refreshPageButton(t('translation:Errors.RestoreInProgress.Action'));
  } else if (isTenantRestoreFailedError(props.error)) {
    //the restore failed and the organization stays locked until a restore succeeds; refreshing shows whether it has
    intent = 'error';
    action = refreshPageButton(t('translation:Errors.RestoreFailed.Action'));
  } else if (isTenantSuspendedError(props.error)) {
    //suspended for a reason of its own (support, non-payment): nothing the user can do from here
    intent = 'error';
  } else if (getIdentityProviderErrorCode(props.error) || isIdentityAuthError(props.error)) {
    //the identity layer refused the sign-in (a personal account, another directory, an admin who has not
    //allowed the app) or the feature needs another identity: sign in differently
    intent = 'error';
  } else if (isNetworkError(props.error) || isAbortOrCancelError(props.error) || isLoginError(props.error)) {
    intent = 'warning';
    action = refreshPageButton(t('translation:Errors.NetworkError.Action'));
  } else {
    switch (props.error.code) {
      case '429': //too many requests
        intent = 'warning';
        action = refreshPageButton(t('translation:Errors.TooManyRequests.Action'));
        break;
      case '404': //not found: another user has deleted the object
        intent = 'warning';
        action = refreshPageButton(t('translation:Errors.DatabaseNotFound.Action'));
        break;
      case '409': //conflict: another user has updated/deleted the object or relationship
        intent = 'warning';
        action = refreshPageButton(t('translation:Errors.DatabaseConflict.Action'));
        break;
      case '401':
      case '403':
        //after a switch of organization a scene of the previous one can still refresh and get a 403 with the
        //new account: start over at the root instead of showing an error
        if (isAuthReloadError(props.error)) {
          window.location.href = '/';

          return null;
        }
        intent = 'error';
        break;
      case '400':
      case '402': //the AI credits are used up (CREDIT0001)
      case '500':
        if (AppError.isControllerException(props.error)) {
          intent = 'warning';
        }
        break;
    }
  }

  const showDebug = !Config.isProd() && !Config.isTest() && props.error.debug;

  const bar = (
    <IPMessageBar intent={intent} layout="multiline">
      <IPMessageBarBody>
        <b>{msg}</b>
      </IPMessageBarBody>
      <IPMessageBarActions
        containerAction={
          showDismiss ? (
            <IPButton
              appearance="transparent"
              title={t('translation:General.Button.Close')}
              icon={<IPIcon name="Dismiss" />}
              onClick={async () => {
                if (props.customAction) {
                  await props.customAction(ICustomErrorActionType.Dismiss);
                } else if (props.setError) {
                  props.setError(undefined);
                }
              }}
            />
          ) : undefined
        }
      >
        {action}
      </IPMessageBarActions>
    </IPMessageBar>
  );

  return (
    <Fragment>
      {showDebug ? (
        <IPTooltip content={props.error.debug ?? ''} relationship="description">
          {bar}
        </IPTooltip>
      ) : (
        bar
      )}
    </Fragment>
  );
};
